import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, AutoComplete, Button, Row, Col, Space, Alert, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, CalculatorOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { dentalService } from '../../services';
import { CHARTS, ITEM_STATUSES, draftTotals, formatMoney, money, translate } from '../../utils/dental';

const { Text } = Typography;

const ALL_TEETH = [...CHARTS.permanent, ...CHARTS.primary]
    .flatMap((jaw) => jaw.flat())
    .sort();

// A schedule for `total`, worked out the way the server's tests say it must
// be: amounts rounded down to the 250 note, the last instalment carrying what
// the rounding left over, so it always adds up to the total exactly.
const buildSchedule = ({ total, downPayment, count, firstDate, intervalMonths }) => {
    const amount = money(total);
    const start = dayjs(firstDate);
    if (amount === 0 || !start.isValid()) return [];

    const down = Math.min(money(downPayment), amount);
    const rest = amount - down;
    const parts = Math.max(1, Math.min(60, Math.floor(Number(count)) || 1));
    const step = Math.max(1, Math.floor(Number(intervalMonths)) || 1);

    const rows = [];
    if (down > 0) rows.push({ dueDate: start.format('YYYY-MM-DD'), amount: down, isDownPayment: true });
    if (rest === 0) return rows;

    const even = Math.floor(rest / parts);
    const rounded = Math.floor(even / 250) * 250;
    const each = rounded > 0 ? rounded : even;
    if (each === 0) return [...rows, { dueDate: start.format('YYYY-MM-DD'), amount: rest }];

    const offset = down > 0 ? 1 : 0;
    for (let index = 0; index < parts; index += 1) {
        rows.push({
            // dayjs keeps the day of the month where the month has it.
            dueDate: start.add((index + offset) * step, 'month').format('YYYY-MM-DD'),
            amount: index === parts - 1 ? rest - each * (parts - 1) : each
        });
    }
    return rows;
};

// Writing or changing a treatment plan: the procedures and what they cost,
// then the instalments the patient will pay in.
const DentalPlanModal = ({ open, plan, patient, procedures = [], onCancel, onSaved }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const items = Form.useWatch('items', form);
    const discount = Form.useWatch('discount', form);
    const installments = Form.useWatch('installments', form);

    const tr = (key, fallback) => translate(t, key, fallback);

    useEffect(() => {
        if (!open) return;
        form.resetFields();
        form.setFieldsValue(plan ? {
            title: plan.title,
            items: plan.items.map((item) => ({ ...item })),
            discount: plan.discount || undefined,
            installments: (plan.installments || []).map((row) => ({
                dueDate: dayjs(row.dueDate).format('YYYY-MM-DD'),
                amount: row.amount,
                isDownPayment: row.isDownPayment
            })),
            notes: plan.notes
        } : {
            items: [{ status: 'planned' }],
            installments: [],
            schedule: { count: 3, intervalMonths: 1, firstDate: dayjs().format('YYYY-MM-DD') }
        });
    }, [open, plan, form]);

    const totals = draftTotals(items, discount);
    const paid = plan?.totals?.paid || 0;
    const scheduled = (installments || []).reduce((sum, row) => sum + money(row?.amount), 0);

    const generate = () => {
        const schedule = form.getFieldValue('schedule') || {};
        const rows = buildSchedule({ ...schedule, total: totals.total });
        if (rows.length === 0) {
            message.warning(tr('dentalNothingToSchedule', 'Add the procedures and their prices first'));
            return;
        }
        form.setFieldsValue({ installments: rows });
    };

    // Picking a procedure from the price list brings its price with it.
    const pickProcedure = (index, name) => {
        const match = procedures.find((procedure) => procedure.name === name);
        if (!match) return;
        const next = [...(form.getFieldValue('items') || [])];
        next[index] = { ...next[index], procedure: name, price: match.price };
        form.setFieldsValue({ items: next });
    };

    const submit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            return;
        }

        const body = {
            title: values.title,
            items: (values.items || []).filter((item) => item?.procedure),
            discount: values.discount || 0,
            installments: (values.installments || []).filter((row) => row?.dueDate && row?.amount),
            notes: values.notes
        };

        setSaving(true);
        try {
            const response = plan
                ? await dentalService.updatePlan(plan._id, body)
                : await dentalService.createPlan({ ...body, patientId: patient._id });
            message.success(t('success'));
            onSaved(response.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={`${plan ? tr('dentalEditPlan', 'Edit treatment plan') : tr('dentalNewPlan', 'New treatment plan')}${patient ? ` — ${patient.name}` : ''}`}
            open={open}
            onCancel={onCancel}
            onOk={submit}
            okText={t('save')}
            confirmLoading={saving}
            width="min(980px, 96vw)"
            style={{ top: 24 }}
            // A half-written plan must not be lost to a stray click.
            maskClosable={false}
            destroyOnClose
        >
            <Form form={form} layout="vertical" size="small">
                <Form.Item name="title" label={tr('dentalPlanTitle', 'Plan name')}>
                    <Input placeholder={tr('dentalPlanTitleHint', 'e.g. Upper right restoration')} />
                </Form.Item>

                <div className="visit-section-title">{tr('dentalProcedures', 'Procedures')}</div>
                <Form.List name="items">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field) => (
                                <Row gutter={8} key={field.key} align="bottom">
                                    <Col xs={8} md={3}>
                                        <Form.Item name={[field.name, 'tooth']} label={tr('tooth', 'Tooth')}>
                                            <Select
                                                allowClear
                                                showSearch
                                                placeholder="-"
                                                options={ALL_TEETH.map((tooth) => ({ value: tooth, label: tooth }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={16} md={9}>
                                        <Form.Item
                                            name={[field.name, 'procedure']}
                                            label={tr('dentalProcedureName', 'Procedure')}
                                            rules={[{ required: true, message: t('required') }]}
                                        >
                                            <AutoComplete
                                                placeholder={tr('dentalProcedureHint', 'Pick from the price list or type')}
                                                options={procedures
                                                    .filter((procedure) => procedure.isActive !== false)
                                                    .map((procedure) => ({
                                                        value: procedure.name,
                                                        label: `${procedure.name} — ${formatMoney(procedure.price)}`
                                                    }))}
                                                filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                                                onSelect={(name) => pickProcedure(field.name, name)}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={10} md={5}>
                                        <Form.Item name={[field.name, 'price']} label={tr('price', 'Price')}>
                                            <InputNumber
                                                min={0}
                                                step={5000}
                                                style={{ width: '100%' }}
                                                formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/,/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={10} md={5}>
                                        <Form.Item name={[field.name, 'status']} label={t('status')}>
                                            <Select options={ITEM_STATUSES.map(([value, key, fallback]) => ({ value, label: tr(key, fallback) }))} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={4} md={2}>
                                        <Form.Item label=" ">
                                            <Button
                                                danger
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                disabled={fields.length === 1}
                                                onClick={() => remove(field.name)}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            ))}
                            <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ status: 'planned' })}>
                                {tr('dentalAddProcedure', 'Add procedure')}
                            </Button>
                        </>
                    )}
                </Form.List>

                <Row gutter={8} style={{ marginTop: 12 }} align="middle">
                    <Col xs={12} md={6}>
                        <Form.Item name="discount" label={tr('discount', 'Discount')}>
                            <InputNumber min={0} step={5000} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={18}>
                        <Space size={16} wrap>
                            <Text type="secondary">{tr('subtotal', 'Subtotal')}: {formatMoney(totals.subtotal)}</Text>
                            <Text strong style={{ fontSize: 16 }}>{tr('total', 'Total')}: {formatMoney(totals.total)}</Text>
                            {paid > 0 && <Text type="success">{tr('paid', 'Paid')}: {formatMoney(paid)}</Text>}
                        </Space>
                    </Col>
                </Row>

                {paid > totals.total && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 8 }}
                        message={tr('dentalPaidMoreThanTotal', 'More has already been paid than this plan now costs.')}
                    />
                )}

                <div className="visit-section-title">{tr('installments', 'Instalments')}</div>
                {/* The quick way: say how many, and the schedule is worked out.
                    Every row stays editable afterwards. */}
                <Row gutter={8} align="bottom">
                    <Col xs={12} md={5}>
                        <Form.Item name={['schedule', 'downPayment']} label={tr('downPayment', 'Down payment')}>
                            <InputNumber min={0} step={5000} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={4}>
                        <Form.Item name={['schedule', 'count']} label={tr('installmentCount', 'Instalments')}>
                            <InputNumber min={1} max={60} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={5}>
                        <Form.Item name={['schedule', 'intervalMonths']} label={tr('installmentEvery', 'Every (months)')}>
                            <InputNumber min={1} max={12} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={5}>
                        <Form.Item name={['schedule', 'firstDate']} label={tr('installmentFirstDate', 'Starting')}>
                            <Input type="date" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={5}>
                        <Form.Item label=" ">
                            <Button block icon={<CalculatorOutlined />} onClick={generate}>
                                {tr('installmentGenerate', 'Work out')}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.List name="installments">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, index) => (
                                <Row gutter={8} key={field.key} align="middle">
                                    <Col xs={2} md={1}><Text type="secondary">{index + 1}</Text></Col>
                                    <Col xs={10} md={7}>
                                        <Form.Item name={[field.name, 'dueDate']} rules={[{ required: true, message: t('required') }]}>
                                            <Input type="date" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={9} md={7}>
                                        <Form.Item name={[field.name, 'amount']} rules={[{ required: true, message: t('required') }]}>
                                            <InputNumber
                                                min={0}
                                                step={5000}
                                                style={{ width: '100%' }}
                                                formatter={(value) => `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={(value) => value.replace(/,/g, '')}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={3} md={2}>
                                        <Form.Item>
                                            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            ))}
                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ dueDate: dayjs().format('YYYY-MM-DD') })}>
                                {tr('installmentAdd', 'Add instalment')}
                            </Button>
                        </>
                    )}
                </Form.List>

                {scheduled > 0 && scheduled !== totals.total && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message={`${tr('installmentMismatch', 'The instalments add up to')} ${formatMoney(scheduled)}, ${tr('installmentMismatchTotal', 'but the plan costs')} ${formatMoney(totals.total)}.`}
                    />
                )}

                <Form.Item name="notes" label={t('notes')} style={{ marginTop: 12 }}>
                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default DentalPlanModal;
